import React, { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase/client';
import { ArrowRight, LinkSimple, MagnifyingGlass, SpinnerGap, X } from '@phosphor-icons/react';
import { statusPill } from '../../../components/ui/statusStyles';
import { STATUS_FARBE } from '../../../lib/tickets/darstellung';
import type { Ticket } from './types';

export interface TicketVerknuepfungenProps {
  ticket: Ticket;
  onCountChange?: (n: number) => void; // damit Parent den Verknüpfungen-Badge kennt
  // Erweiterung ggü. Spec-Skelett (siehe .superpowers/sdd/task-3-report.md, gleiches Muster wie
  // TicketAnhaengeProps.onUpdate in task-2-report.md und TicketEigenschaftenProps.onUpdate in
  // task-1-report.md): Verknüpfen/Entfernen hat im Panel bisher IMMER den Parent-Refetch
  // (onUpdate) ausgelöst — diese Kopplung ist real (TicketListe.handleTicketUpdated), also wird
  // sie unverändert durchgereicht statt stillschweigend entfernt.
  onUpdate?: () => void;
  // userProfile.id wird für benutzer_id in den ticket_historie-Einträgen benötigt (gleiches
  // Muster wie TicketAnhaengeProps.userProfile).
  userProfile?: any;
}

export default function TicketVerknuepfungen({ ticket, onCountChange, onUpdate, userProfile }: TicketVerknuepfungenProps) {
  const [verknuepfteTickets, setVerknuepfteTickets] = useState<{ id: string; ticket_nummer: string; titel: string; status: string; richtung: 'ausgehend' | 'eingehend' }[]>([]);
  const [linkSearch, setLinkSearch] = useState('');
  const [linkResults, setLinkResults] = useState<{ id: string; ticket_nummer: string; titel: string; status: string }[]>([]);
  const [linkSearching, setLinkSearching] = useState(false);

  const fetchVerknuepfteTickets = async () => {
    const results: typeof verknuepfteTickets = [];

    // Ausgehend: dieses Ticket verweist auf ein anderes
    if (ticket.verknuepft_mit) {
      const { data } = await supabase
        .from('tickets')
        .select('id, ticket_nummer, titel, status')
        .eq('id', ticket.verknuepft_mit)
        .single();
      if (data) results.push({ ...data, richtung: 'ausgehend' });
    }

    // Eingehend: andere Tickets verweisen auf dieses
    const { data: eingehend } = await supabase
      .from('tickets')
      .select('id, ticket_nummer, titel, status')
      .eq('verknuepft_mit', ticket.id);
    if (eingehend) {
      eingehend.forEach(t => results.push({ ...t, richtung: 'eingehend' }));
    }

    setVerknuepfteTickets(results);
    onCountChange?.(results.length);
  };

  useEffect(() => {
    fetchVerknuepfteTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket.id]);

  const handleLinkSearch = async (query: string) => {
    setLinkSearch(query);
    if (query.trim().length < 2) {
      setLinkResults([]);
      return;
    }
    setLinkSearching(true);
    const { data } = await supabase
      .from('tickets')
      .select('id, ticket_nummer, titel, status')
      .neq('id', ticket.id)
      .or(`ticket_nummer.ilike.%${query}%,titel.ilike.%${query}%`)
      .limit(5);
    setLinkResults(data || []);
    setLinkSearching(false);
  };

  const handleLinkTicket = async (targetId: string) => {
    const targetNr = linkResults.find(r => r.id === targetId)?.ticket_nummer || targetId;

    // Dieses Ticket → Ziel
    await supabase.from('tickets').update({ verknuepft_mit: targetId }).eq('id', ticket.id);
    await supabase.from('ticket_historie').insert({
      ticket_id: ticket.id,
      benutzer_id: userProfile?.id,
      aktion: 'verknuepfung',
      neuer_wert: `Verknüpft mit ${targetNr}`,
    });

    // Ziel → Dieses Ticket (bidirektional)
    const { data: target } = await supabase.from('tickets').select('verknuepft_mit').eq('id', targetId).single();
    if (target && !target.verknuepft_mit) {
      await supabase.from('tickets').update({ verknuepft_mit: ticket.id }).eq('id', targetId);
      await supabase.from('ticket_historie').insert({
        ticket_id: targetId,
        benutzer_id: userProfile?.id,
        aktion: 'verknuepfung',
        neuer_wert: `Verknüpft mit ${ticket.ticket_nummer}`,
      });
    }

    setLinkSearch('');
    setLinkResults([]);
    onUpdate?.();
  };

  const handleUnlinkTicket = async (linkedTicket: typeof verknuepfteTickets[0]) => {
    if (linkedTicket.richtung === 'ausgehend') {
      // Eigene Verknüpfung entfernen
      await supabase.from('tickets').update({ verknuepft_mit: null }).eq('id', ticket.id);
      // Gegenseite auch entfernen falls sie auf uns zeigt
      const { data: other } = await supabase.from('tickets').select('verknuepft_mit').eq('id', linkedTicket.id).single();
      if (other?.verknuepft_mit === ticket.id) {
        await supabase.from('tickets').update({ verknuepft_mit: null }).eq('id', linkedTicket.id);
      }
    } else {
      // Eingehende Verknüpfung: beim anderen Ticket entfernen
      await supabase.from('tickets').update({ verknuepft_mit: null }).eq('id', linkedTicket.id);
      // Eigene auch entfernen falls sie auf das andere zeigt
      if (ticket.verknuepft_mit === linkedTicket.id) {
        await supabase.from('tickets').update({ verknuepft_mit: null }).eq('id', ticket.id);
      }
    }

    await supabase.from('ticket_historie').insert({
      ticket_id: ticket.id,
      benutzer_id: userProfile?.id,
      aktion: 'verknuepfung_entfernt',
      neuer_wert: `Verknüpfung mit ${linkedTicket.ticket_nummer} entfernt`,
    });

    onUpdate?.();
  };

  const punkt = (status: string) => (
    <span aria-hidden data-tip={statusPill(status).label} className="shrink-0"
      style={{ width: 8, height: 8, borderRadius: '50%', background: STATUS_FARBE[status] || 'var(--ink3)' }} />
  );
  const zeile: React.CSSProperties = { gap: 10, padding: '8px 10px', borderRadius: 14, background: 'var(--fill)' };
  const neueTreffer = linkResults.filter(r => !verknuepfteTickets.some(vt => vt.id === r.id));

  return (
    <div className="flex flex-col" style={{ gap: 10 }}>
      {/* Verknüpfte Tickets anzeigen */}
      {verknuepfteTickets.length > 0 && (
        <div className="flex flex-col" style={{ gap: 6 }}>
          {verknuepfteTickets.map(vt => (
            <div key={vt.id} className="flex items-center" style={zeile}>
              {punkt(vt.status)}
              <span className="flex min-w-0 flex-1 items-center" style={{ gap: 8, color: 'var(--ink)' }}>
                <span className="shrink-0" style={{ fontSize: 12, fontWeight: 800, color: 'var(--ink3)' }}>{vt.ticket_nummer}</span>
                <span className="min-w-0 flex-1 truncate" style={{ fontSize: 13, fontWeight: 700 }}>{vt.titel}</span>
              </span>
              <button
                type="button"
                onClick={() => handleUnlinkTicket(vt)}
                aria-label="Verknüpfung entfernen"
                data-tip="Verknüpfung entfernen"
                className="inline-flex shrink-0 items-center justify-center"
                style={{ width: 24, height: 24, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'var(--fill2)', color: 'var(--ink2)' }}
              >
                <X size={12} weight="bold" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Ticket-Suche zum Verknüpfen */}
      <div className="relative flex items-center">
        <MagnifyingGlass size={15} weight="bold" aria-hidden className="pointer-events-none absolute" style={{ left: 11, color: 'var(--ink3)' }} />
        <input
          type="text"
          value={linkSearch}
          onChange={(e) => handleLinkSearch(e.target.value)}
          placeholder="Ticket suchen (Nr. oder Titel)..."
          aria-label="Ticket suchen"
          data-tip="Ticket suchen – Nach Nummer oder Titel, um es zu verknüpfen"
          style={{
            height: 38, width: '100%', borderRadius: 11, padding: '0 34px', border: 'none', outline: 'none', background: 'var(--bg)',
            boxShadow: 'inset 0 0 0 1px var(--line)', color: 'var(--ink)', fontFamily: 'inherit', fontSize: 13.5, minWidth: 0,
          }}
        />
        {linkSearching && (
          <SpinnerGap size={14} className="pointer-events-none absolute animate-spin" style={{ right: 11, color: 'var(--ink3)' }} />
        )}
      </div>

      {/* Suchergebnisse */}
      {neueTreffer.length > 0 && (
        <div className="flex flex-col" style={{ gap: 6 }}>
          {neueTreffer.map(r => (
            <button
              key={r.id}
              type="button"
              onClick={() => handleLinkTicket(r.id)}
              data-tip="Verknüpfen – Dieses Ticket mit dem aktuellen verbinden"
              className="flex w-full items-center text-left"
              style={{ ...zeile, border: 'none', cursor: 'pointer', color: 'var(--ink)', fontFamily: 'inherit', boxShadow: 'inset 0 0 0 1px var(--line)' }}
            >
              <LinkSimple size={14} weight="bold" className="shrink-0" style={{ color: 'var(--acc)' }} />
              {punkt(r.status)}
              <span className="shrink-0" style={{ fontSize: 12, fontWeight: 800, color: 'var(--ink3)' }}>{r.ticket_nummer}</span>
              <span className="min-w-0 flex-1 truncate" style={{ fontSize: 13, fontWeight: 700 }}>{r.titel}</span>
              <ArrowRight size={13} weight="bold" className="shrink-0" style={{ color: 'var(--ink3)' }} />
            </button>
          ))}
        </div>
      )}

      {linkSearch.length >= 2 && linkResults.length === 0 && !linkSearching && (
        <p className="text-center" style={{ margin: 0, padding: '6px 0', fontSize: 13, fontWeight: 600, color: 'var(--ink3)' }}>Keine Tickets gefunden</p>
      )}
    </div>
  );
}
